import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { auth as adminAuth } from '../../admin-auth/adminAppAuth';
import { createUserWithEmailAndPassword } from '../../admin-auth/adminAuthApi';
import { fetchAdminMe } from '../../api/adminMeApi';
import { fetchAdminSetupAvailable } from '../../api/adminSetupApi';
import { ADMIN_PATHS, adminPath } from '../../constants/adminPaths';
import { getAuthErrorMessage } from '../../utils/authErrorUtils';
import '../../admin-auth/styles/adminLoginPage.css';

const AdminSetup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    fetchAdminSetupAvailable()
      .then((available) => {
        if (cancelled) return;
        if (!available) {
          navigate(adminPath(ADMIN_PATHS.LOGIN), { replace: true });
          return;
        }
        setChecking(false);
      })
      .catch(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await createUserWithEmailAndPassword(adminAuth, email, password);
      await fetchAdminMe();
      navigate(adminPath(ADMIN_PATHS.DASHBOARD), { replace: true });
    } catch (err) {
      setError(getAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="adminLogin">
        <div className="adminLogin__bg" aria-hidden="true" />
        <main className="adminLogin__container">
          <section className="adminLogin__card" aria-label="Admin setup">
            <p className="adminLogin__subtitle">Checking setup…</p>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="adminLogin">
      <div className="adminLogin__bg" aria-hidden="true" />
      <main className="adminLogin__container">
        <section className="adminLogin__card" aria-label="Create first admin">
          <header className="adminLogin__header">
            <div className="adminLogin__badge" aria-hidden="true">
              Setup
            </div>
            <h1 className="adminLogin__title">Create first admin</h1>
            <p className="adminLogin__subtitle">
              This page works once. Pick the email and password you will use to open the admin panel.
            </p>
          </header>

          {error ? (
            <div className="adminLogin__alert" role="alert">
              <span className="adminLogin__alertDot" aria-hidden="true" />
              <span>{error}</span>
            </div>
          ) : null}

          <form className="adminLogin__form" onSubmit={handleSubmit}>
            <label className="adminLogin__label">
              <span className="adminLogin__labelText">Email</span>
              <input
                className="adminLogin__input"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
              />
            </label>

            <label className="adminLogin__label">
              <span className="adminLogin__labelRow">
                <span className="adminLogin__labelText">Password</span>
                <button
                  type="button"
                  className="adminLogin__linkBtn"
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </span>
              <input
                className="adminLogin__input"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
              />
            </label>

            <label className="adminLogin__label">
              <span className="adminLogin__labelText">Confirm password</span>
              <input
                className="adminLogin__input"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat password"
                required
              />
            </label>

            <button className="adminLogin__submit" type="submit" disabled={loading}>
              {loading ? 'Creating…' : 'Create admin account'}
            </button>
          </form>

          <footer className="adminLogin__footer">
            <RouterLink className="adminLogin__secondaryLink" to={adminPath(ADMIN_PATHS.LOGIN)}>
              Already have an account? Sign in
            </RouterLink>
          </footer>
        </section>
      </main>
    </div>
  );
};

export default AdminSetup;
